import React, { useEffect, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { PageHeader, Card, Spinner } from "@/components/UIKit";
import { toast } from "sonner";
import { Zap, TrendingUp, Mail, MessageSquare, FileCheck, Calculator, BookOpen, Landmark, Loader2, ShieldCheck, ShieldOff, AlertCircle, Upload, CheckCircle2 } from "lucide-react";

const ICONS = {
  elhub: Zap, nord_pool: TrendingUp, resend: Mail, twilio: MessageSquare,
  peppol_ehf: FileCheck, tripletex: Calculator, fiken: BookOpen, poweroffice: Calculator, avtalegiro: Landmark, efaktura: FileCheck,
};

export default function Integrasjoner() {
  const [schemas, setSchemas] = useState({});
  const [state, setState] = useState({});
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ enabled: false, sandbox: true, config: {} });

  const load = async () => {
    const [s, st] = await Promise.all([api.get("/integrations/schemas"), api.get("/integrations")]);
    setSchemas(s.data); setState(st.data);
  };
  useEffect(() => { load(); }, []);

  const openConfig = (kind) => {
    setActive(kind);
    const cur = state[kind] || { enabled: false, sandbox: true, config: {} };
    setForm({ enabled: !!cur.enabled, sandbox: cur.sandbox !== false, config: { ...cur.config } });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/integrations/${active}`, { enabled: form.enabled, sandbox: form.sandbox, config: form.config });
      toast.success(`${schemas[active].label} lagret`);
      await load();
      setActive(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setBusy(false); }
  };

  const test = async (kind) => {
    try {
      const { data } = await api.post(`/integrations/${kind}/test`);
      toast.success(data.message || "Tilkobling OK");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Testet feilet"); }
  };

  return (
    <div data-testid="integrasjoner-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(schemas).map(([kind, schema]) => {
          const s = state[kind] || {};
          const Icon = ICONS[kind] || Zap;
          return (
            <Card key={kind} className="p-5" testid={`integration-${kind}`}>
              <div className="flex items-start gap-3">
                <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 border ${s.enabled ? "bg-emerald-500/10 border-emerald-500/30" : "bg-zinc-900 border-zinc-800"}`}>
                  <Icon className={`h-5 w-5 ${s.enabled ? "text-emerald-400" : "text-zinc-500"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-heading font-medium text-zinc-100">{schema.label}</div>
                    {s.enabled ? (
                      s.sandbox
                        ? <span className="text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20 px-1.5 py-0.5 rounded">Sandbox</span>
                        : <span className="text-[10px] uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded">Produksjon</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded">Deaktivert</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{schema.description}</div>
                  {s.last_error_message && (
                    <div className="text-[11px] text-red-400 mt-2 flex items-start gap-1"><AlertCircle className="h-3 w-3 mt-0.5 shrink-0" /> {s.last_error_message}</div>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => openConfig(kind)}
                      className="h-8 px-3 rounded-md border border-zinc-800 hover:border-emerald-500/40 text-zinc-300 text-xs" data-testid={`configure-${kind}`}>
                      Konfigurer
                    </button>
                    {s.enabled && (
                      <button onClick={() => test(kind)}
                        className="h-8 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-xs">
                        Test tilkobling
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Config modal */}
      {active && schemas[active] && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActive(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-[#0a0a0a] border border-zinc-800 rounded-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-zinc-900">
              <div className="font-heading text-lg font-semibold">{schemas[active].label}</div>
              <div className="text-xs text-zinc-500 mt-1">{schemas[active].description}</div>
            </div>
            <div className="p-5 space-y-3">
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="cfg-enabled" />
                <span className="text-sm">Aktiver integrasjonen</span>
              </label>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={form.sandbox} onChange={(e) => setForm({ ...form, sandbox: e.target.checked })} data-testid="cfg-sandbox" />
                <span className="text-sm">Sandbox / test-modus <span className="text-[10px] text-zinc-500">(anbefalt før du bytter til produksjon)</span></span>
              </label>
              {schemas[active].fields.map((f) => (
                <div key={f.name}>
                  <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={4} value={form.config[f.name] || ""} onChange={(e) => setForm({ ...form, config: { ...form.config, [f.name]: e.target.value } })}
                      placeholder={f.placeholder}
                      className="w-full px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm font-mono" data-testid={`cfg-${f.name}`} />
                  ) : (
                    <input type={f.type === "password" ? "password" : "text"} value={form.config[f.name] || ""}
                      onChange={(e) => setForm({ ...form, config: { ...form.config, [f.name]: e.target.value } })}
                      placeholder={f.placeholder}
                      className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" data-testid={`cfg-${f.name}`} />
                  )}
                </div>
              ))}
              {active === "elhub" && <ElhubCertUpload onUploaded={() => api.get("/integrations").then(({data}) => setState(data))} />}
              <div className="text-[11px] text-zinc-500 pt-2 border-t border-zinc-900">
                <ShieldCheck className="h-3 w-3 inline text-emerald-400" /> Hemmeligheter lagres kryptert (Fernet) og vises aldri i klartekst etter lagring.
              </div>
            </div>
            <div className="p-4 border-t border-zinc-900 flex justify-end gap-2">
              <button onClick={() => setActive(null)} className="h-9 px-4 rounded-md border border-zinc-800 text-sm">Avbryt</button>
              <button onClick={save} disabled={busy} className="h-9 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium disabled:opacity-60 flex items-center gap-2" data-testid="cfg-save">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Lagre
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ElhubCertUpload({ onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [goingLive, setGoingLive] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/elhub/certificate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStatus(data);
      toast.success(`Sertifikat lastet opp (${(data.size_bytes / 1024).toFixed(1)} kB)`);
      onUploaded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setUploading(false); }
  };

  const goLive = async () => {
    setGoingLive(true);
    try {
      await api.post("/elhub/go-live");
      toast.success("Elhub er nå i produksjonsmodus");
      onUploaded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mangler noe før produksjon"); }
    finally { setGoingLive(false); }
  };

  return (
    <div className="pt-3 border-t border-zinc-900">
      <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Last opp PKCS12/PEM-sertifikat</label>
      <div className="flex items-center gap-2">
        <label className="h-10 px-4 rounded-md border border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-400 text-sm font-medium flex items-center gap-2 cursor-pointer">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Velg fil (.p12/.pfx/.pem)
          <input type="file" accept=".p12,.pfx,.pem,.crt,.cer" className="hidden" data-testid="elhub-cert-file"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
        </label>
        <button onClick={goLive} disabled={goingLive}
          className="h-10 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-sm font-medium flex items-center gap-2"
          data-testid="elhub-go-live">
          {goingLive ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          Sett i produksjon
        </button>
      </div>
      {status && (
        <div className="text-xs text-emerald-400 mt-2 flex items-center gap-1.5">
          <CheckCircle2 className="h-3 w-3" /> {status.filename} · {(status.size_bytes / 1024).toFixed(1)} kB · sha256:{status.sha256_prefix}
        </div>
      )}
      <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
        For produksjon må api_url, client_id, client_secret, party_id (GLN) og sertifikat være satt. Sertifikatet lagres kryptert.
      </div>
    </div>
  );
}
